import { z } from 'zod';

/** API/Mongo dates often arrive as ISO strings or Date objects. */
const DateLikeSchema = z.union([z.string(), z.date()]);

export const SubmissionTypeSchema = z.enum(['thumbtack', 'partner', 'qwikfix', 'other']);
export type SubmissionType = z.infer<typeof SubmissionTypeSchema>;

export const QuoteRequestSchema = z.object({
  _id: z.string().optional(),
  _repairCompanyId: z.string(),
  repairItemIds: z.array(z.string()),
  requestedAt: DateLikeSchema,
  status: z.enum(['pending', 'received', 'expired']),
  emailJobId: z.string().optional(),
  resendMessageId: z.string().optional(),
  pdfUrl: z.string().optional(),
  pdfKey: z.string().optional(),
  quoteResponseUrl: z.string().optional(),
  quotedAmount: z.number().optional(),
  quotedAt: DateLikeSchema.optional(),
  notes: z.string().optional(),
  createdAt: DateLikeSchema.optional(),
  updatedAt: DateLikeSchema.optional(),
});
export type QuoteRequest = z.infer<typeof QuoteRequestSchema>;

export const CategoryMatchSchema = z.object({
  categoryId: z.string(),
  confidence: z.number().min(0).max(1),
});
export type CategoryMatch = z.infer<typeof CategoryMatchSchema>;

export const CategoryAssignmentSchema = z.object({
  repairItemId: z.string(),
  selectedCategoryId: z.string(),
  topMatches: z.array(CategoryMatchSchema),
  isManualOverride: z.boolean(),
  includedInQuotes: z.boolean().optional(),
});
export type CategoryAssignment = z.infer<typeof CategoryAssignmentSchema>;

/** Known providerMetadata keys; passthrough keeps future provider fields. */
export const QuoteSubmissionProviderMetadataSchema = z
  .object({
    thumbtackCategoryId: z.string().optional(),
    businessId: z.string().optional(),
    businessName: z.string().optional(),
    widgetUrl: z.string().optional(),
    searchId: z.string().optional(),
    thumbtackRequestCreatedEvent: z
      .object({
        businesses_contacted: z
          .array(
            z.object({
              business_pk: z.string(),
              business_name: z.string(),
            }),
          )
          .optional(),
        category_pk: z.string().optional(),
        zip_code: z.string().optional(),
        user_pk: z.string().optional(),
        created_at: z.number().optional(),
        is_existing_user: z.boolean().optional(),
        search_id: z.string().optional(),
        request_pk: z.string().optional(),
      })
      .passthrough()
      .optional(),

    provider: z.literal('qwikFix').optional(),
    qwikQuoteUuid: z.string().optional(),
    requestUuid: z.string().optional(),
    dashboardUrl: z.string().optional(),
    qwikFixRequestStatus: z.string().optional(),
    qwikFixStatusMessage: z.string().optional(),
    qwikFixStatusUpdatedAt: DateLikeSchema.optional(),
    qwikFixWebhookHistory: z
      .array(
        z.object({
          requestStatus: z.string(),
          message: z.string().optional(),
          receivedAt: DateLikeSchema,
          automationEventHistoryId: z.string().optional(),
        }),
      )
      .optional(),

    repairRequestPdfUrl: z.string().optional(),
    repairRequestPdfKey: z.string().optional(),

    locationMetadata: z
      .object({
        zipCode: z.string(),
        requestLocation: z.string(),
      })
      .optional(),
  })
  .passthrough();
export type QuoteSubmissionProviderMetadata = z.infer<
  typeof QuoteSubmissionProviderMetadataSchema
>;

export const QuoteSubmissionSchema = z.object({
  _id: z.string().optional(),
  submissionType: SubmissionTypeSchema,
  categoryId: z.string().optional(),
  submittedBy: z.string().optional(),
  submittedAt: DateLikeSchema,
  includedItemIds: z.array(z.string()),
  providerMetadata: QuoteSubmissionProviderMetadataSchema,
  createdAt: DateLikeSchema.optional(),
  updatedAt: DateLikeSchema.optional(),
});
export type QuoteSubmission = z.infer<typeof QuoteSubmissionSchema>;

/** Shared fields on every repair-list line item. */
export const RepairItemMetaSchema = z.object({
  sectionName: z.string(),
  notes: z.string().optional(),
  reportId: z.string().optional(),
  reportName: z.string().optional(),
  addendumType: z.enum(['repair', 'credit']).optional(),
  addendumCreditAmount: z.number().optional(),
});
export type RepairItemMeta = z.infer<typeof RepairItemMetaSchema>;

/**
 * Spectora portal Observation photo shape (flattened onto Spectora repair items).
 * Kept loose — historical lists vary in which URLs are present.
 */
export const SpectoraRepairPhotoSchema = z
  .object({
    id: z.string(),
    image: z.string().optional(),
    photo: z.string().optional(),
    original_image_url: z.string().optional(),
    edited_image_url: z.string().optional(),
    small_image_url: z.string().optional(),
    small_edited_image_url: z.string().optional(),
    priority: z.number().optional(),
    caption: z.string().nullish(),
    is_pano: z.boolean().optional(),
    video: z.string().optional(),
    video_url: z.string().optional(),
    video_thumb: z.string().optional(),
  })
  .passthrough();
export type SpectoraRepairPhoto = z.infer<typeof SpectoraRepairPhotoSchema>;

/**
 * Spectora-sourced repair item (classic portal Observation + meta).
 * Saved lists without `source` are treated as this shape.
 */
export const SpectoraRepairItemSchema = RepairItemMetaSchema.extend({
  source: z.literal('spectora').optional(),
  id: z.string(),
  identifier: z.string().optional(),
  name: z.string(),
  item_name: z.string().optional(),
  location: z.string().nullish(),
  comment_type: z.string().optional(),
  category: z.number().optional(),
  value: z.string().nullish(),
  value2: z.string().nullish(),
  unit_type: z.string().nullish(),
  unit_type_choices: z.string().nullish(),
  answer_type: z.string().optional(),
  text: z.string().nullish(),
  recommendation: z.string().optional(),
  recommendation_id: z.union([z.string(), z.number()]).optional(),
  estimate_min: z.number().optional(),
  estimate_max: z.number().optional(),
  estimate: z.string().optional(),
  priority: z.number().optional(),
  photo_count: z.number().optional(),
  photos: z.array(SpectoraRepairPhotoSchema).optional(),
  choices: z.string().nullish(),
  template_comment_id: z.number().optional(),
  display_estimate: z.boolean().optional(),
  defectIdx: z.string().optional(),
}).passthrough();
export type SpectoraRepairItem = z.infer<typeof SpectoraRepairItemSchema>;

/**
 * Attik observation nested on Attik repair items (matches FE AttikReportObservation).
 * Canonical shared shape so FE/BE accessors stay aligned.
 */
export const AttikRepairObservationSchema = z
  .object({
    commentId: z.string(),
    commentType: z.enum(['info', 'defect', 'limitation']),
    templateCommentId: z.string().optional(),
    defectLevel: z.string().optional(),
    narrative: z.string(),
    finalNarrative: z.string().optional(),
    location: z.string().optional(),
    order: z.number().optional(),
    mediaIds: z.array(z.string()),
    media: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
          caption: z.string().optional(),
        }),
      )
      .optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
    status: z.string().optional(),
    editable: z.boolean().optional(),
    title: z.string().optional(),
    legacySkin: z
      .object({
        identifier: z.string().optional(),
        name: z.string().optional(),
        answer_type: z.string().optional(),
        value: z.string().nullish(),
        value2: z.string().nullish(),
        choices: z.string().nullish(),
        text: z.string().nullish(),
        recommendation: z.string().nullish(),
        category: z.number().nullish(),
      })
      .optional(),
  })
  .passthrough();
export type AttikRepairObservation = z.infer<typeof AttikRepairObservationSchema>;

/**
 * Attik-native repair item — stores the native observation as-is (no Spectora map).
 */
export const AttikRepairItemSchema = RepairItemMetaSchema.extend({
  source: z.literal('attik'),
  /** Same as observation.commentId — used by isInRepairList / quote item ids */
  id: z.string(),
  observation: AttikRepairObservationSchema,
  itemName: z.string(),
});
export type AttikRepairItem = z.infer<typeof AttikRepairItemSchema>;

/**
 * Tagged union persisted on repair lists.
 * Prefer checking `source === 'attik'` (Attik) vs missing/`spectora` (Spectora).
 */
export const RepairItemSchema = z.union([
  AttikRepairItemSchema,
  SpectoraRepairItemSchema,
]);
export type RepairItem = AttikRepairItem | SpectoraRepairItem;

export function isAttikRepairItem(item: RepairItem): item is AttikRepairItem {
  return (item as AttikRepairItem).source === 'attik';
}

export function isSpectoraRepairItem(
  item: RepairItem,
): item is SpectoraRepairItem {
  return !isAttikRepairItem(item);
}

export const RepairListStatusSchema = z.enum([
  'draft',
  'quotes_requested',
  'quotes_received',
  'completed',
]);
export type RepairListStatus = z.infer<typeof RepairListStatusSchema>;

export const CategorizationStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);
export type CategorizationStatus = z.infer<typeof CategorizationStatusSchema>;

/**
 * Persisted repair list document (create/patch may omit `_id` until saved).
 * Prefer {@link SavedRepairList} when the document is known to exist.
 */
export const RepairListSchema = z.object({
  _id: z.string().optional(),
  _companyId: z.string(),
  _inspectionId: z.string().optional(),
  reportId: z.string(),
  name: z.string(),
  repairItems: z.array(RepairItemSchema),
  quoteRequests: z.array(QuoteRequestSchema),
  status: RepairListStatusSchema,
  lastQuoteRequestDate: DateLikeSchema.optional(),
  createdBy: z.string().optional(),
  isLocked: z.boolean().optional(),
  categoryAssignments: z.array(CategoryAssignmentSchema).optional(),
  categorizationStatus: CategorizationStatusSchema.optional(),
  quoteSubmissions: z.array(QuoteSubmissionSchema).optional(),
  isSample: z.boolean().optional(),
  expiresAt: DateLikeSchema.optional(),
  createdAt: DateLikeSchema.optional(),
  updatedAt: DateLikeSchema.optional(),
});
export type RepairList = z.infer<typeof RepairListSchema>;

/** Saved repair list as returned from the API (has `_id`). */
export const SavedRepairListSchema = RepairListSchema.extend({
  _id: z.string(),
  createdAt: DateLikeSchema,
  updatedAt: DateLikeSchema,
});
export type SavedRepairList = z.infer<typeof SavedRepairListSchema>;
